// ============================================================================
// RECEIPT VOUCHER (ADR-0054) — the one-step incoming-money document, mirror of the
// Payment Voucher. Settle invoices (at their booked rates), take an advance, record
// other income, less WHT / bank charges / discount — the cash debited to the bank
// is derived, never typed. The Treasury receipt (ADR-0024) remains the two-step flow.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption,
  ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan } from 'components/enterprise-shell';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { receiptVoucherService, partyService } from 'api/services';
import type { ReceiptVoucherDto, ReceiptVoucherQueryParams } from 'api/types';
import { ReceiptVoucherFormModal, ReceiptVoucherViewModal } from '../../../modals/modules/accounts/ReceiptVoucherFormModal';

type ViewMode = 'list' | 'grid';

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const money = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'receiptNumber', label: 'Receipt No' },
  { id: 'receiptDate', label: 'Date' },
  { id: 'partyName', label: 'Received From' },
  { id: 'depositToLedgerName', label: 'Deposited To' },
  { id: 'instrument', label: 'Instrument' },
  { id: 'grossAmount', label: 'Gross' },
  { id: 'deductionTotal', label: 'Deductions' },
  { id: 'netReceived', label: 'Net Received' },
  { id: 'docStatus', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface ScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string; }

const ReceiptVoucherScreen: React.FC<ScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const can = useCan('receipt-voucher');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<ReceiptVoucherQueryParams>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<ReceiptVoucherDto[]>([]);
  const [pagination, setPagination] = useState({ totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('receipt-voucher-columns', COLUMN_DEFS);

  // --- filter panel: draft values apply only on Apply (consistent with the reports) ---
  const [filterOpen, setFilterOpen] = useState(false);
  const [partyFilter, setPartyFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState<ReceiptVoucherDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await receiptVoucherService.getAll(queryParams);
      setData(res.data);
      setPagination({ totalItems: res.pagination.totalItems });
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message || 'Could not load receipts.');
      setData([]);
    } finally { setLoading(false); }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleAdd = useCallback(() => {
    if (formOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormOpen(true);
  }, [formOpen, tabId, isMinimized]);

  const handleRestoreModal = useCallback((instanceId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    setFormOpen(true);
  }, []);

  const handleView = useCallback((r: ReceiptVoucherDto) => {
    const viewId = tabId && r.receiptNumber ? `${tabId}-view-modal-${r.receiptNumber}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setSelected(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((receiptNumber: string) => {
    const rec = data.find(p => p.receiptNumber === receiptNumber);
    if (rec) { setSelected(rec); setViewOpen(true); }
  }, [data]);

  const handleCancel = useCallback(async (r: ReceiptVoucherDto) => {
    const ok = await confirmDelete({
      resourceLabel: `receipt ${r.receiptNumber || ''}`, confirmLabel: 'Cancel Receipt',
    });
    if (!ok) return;
    try {
      await receiptVoucherService.cancel(r.id, r.rowVersion);
      notificationService.toast.success('Receipt cancelled — the bills it settled are open again.');
      load();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message || 'Could not cancel the receipt.', 7000);
      if (e?.response?.status === 409) load();
    }
  }, [confirmDelete, load]);

  const columns: SyutGridColumn<ReceiptVoucherDto>[] = useMemo(() => [
    {
      id: 'receiptNumber', header: 'Receipt No', accessorKey: 'receiptNumber', size: 150,
      cell: ({ row }) => (row.original as ReceiptVoucherDto).receiptNumber || '—',
    },
    {
      id: 'receiptDate', header: 'Date', accessorKey: 'receiptDate', size: 110,
      cell: ({ row }) => fmtDate((row.original as ReceiptVoucherDto).receiptDate),
    },
    {
      id: 'partyName', header: 'Received From', accessorKey: 'partyName', size: 200,
      cell: ({ row }) => (row.original as ReceiptVoucherDto).partyName || '—',
    },
    {
      id: 'depositToLedgerName', header: 'Deposited To', accessorKey: 'depositToLedgerName', size: 180,
      cell: ({ row }) => (row.original as ReceiptVoucherDto).depositToLedgerName || '—',
    },
    {
      id: 'instrument', header: 'Instrument', accessorKey: 'instrumentType', size: 150,
      cell: ({ row }) => {
        const p = row.original as ReceiptVoucherDto;
        if (!p.instrumentType) return '—';
        return p.instrumentNumber ? `${p.instrumentType} · ${p.instrumentNumber}` : p.instrumentType;
      },
    },
    {
      id: 'grossAmount', header: 'Gross', accessorKey: 'grossAmount', size: 120,
      cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{money((row.original as ReceiptVoucherDto).grossAmount)}</span>,
    },
    {
      id: 'deductionTotal', header: 'Deductions', accessorKey: 'deductionTotal', size: 120,
      cell: ({ row }) => {
        const d = (row.original as ReceiptVoucherDto).deductionTotal;
        return <span style={{ display: 'block', textAlign: 'right', color: d ? '#dc2626' : 'var(--text-muted)' }}>{money(d)}</span>;
      },
    },
    {
      id: 'netReceived', header: 'Net Received', accessorKey: 'netReceived', size: 140,
      cell: ({ row }) => {
        const p = row.original as ReceiptVoucherDto;
        return <span style={{ display: 'block', textAlign: 'right', fontWeight: 600 }}>{money(p.netReceived)} {p.currencyCode || ''}</span>;
      },
    },
    {
      id: 'docStatus', header: 'Status', accessorKey: 'docStatus', size: 130,
      cell: ({ row }) => {
        const p = row.original as ReceiptVoucherDto;
        return (
          <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center' }}>
            <span className="badge">{p.docStatus}</span>
            
          </span>
        );
      },
    },
  ], []);

  const rowActions = useCallback((r: ReceiptVoucherDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    // A posted receipt is never edited — cancel it and re-enter (ADR-0054).
    if (can('DELETE') && r.docStatus === 'POSTED') {
      items.push({
        id: 'cancel', label: 'Cancel', icon: <IconSystem.trash size={18} />,
        onClick: () => handleCancel(r), variant: 'danger',
      });
    }
    return items;
  }, [can, handleView, handleCancel]);

  const loadPartyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined });
    return [
      { value: '', label: 'All payers' },
      ...res.data.map(p => ({ value: p.id, label: `${p.partyCode} — ${p.partyName}` })),
    ];
  }, []);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'New Receipt'), visible: can('CREATE') },
    actionButtonFactory.refresh(load),
  ];

  const appliedCount = [queryParams.partyId, queryParams.fromDate, queryParams.toDate, queryParams.includeCancelled]
    .filter(Boolean).length;

  const applyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      partyId: partyFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      includeCancelled: includeCancelled || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [partyFilter, fromDate, toDate, includeCancelled]);

  const clearFilters = useCallback(() => {
    setPartyFilter(''); setFromDate(''); setToDate(''); setIncludeCancelled(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
  }, []);

  return (
    <EnterpriseScreenShell
      title="Receipt Vouchers"
      description="Money received and settled in one step — invoices at their booked rates, advances, other income, less deductions"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.dollarSign size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search receipt no / payer / narration / reference...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Received From" isAsync loadOptions={loadPartyOptions} defaultOptions cacheOptions={false}
                  value={partyFilter} onChange={v => setPartyFilter((v as string) || '')}
                  placeholder="All payers" isClearable size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled}
                    onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            frozenRightColumns={['actions']}
            emptyMessage={loading ? 'Loading...' : 'No receipts yet — click New Receipt to post one.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.dollarSign size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No receipts yet.'}</p>
                </div>
              ) : data.map(p => (
                <div key={p.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {p.receiptNumber || '—'} · {fmtDate(p.receiptDate)}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>
                        {money(p.netReceived)} {p.currencyCode || ''}
                      </h3>
                    </div>
                    <ActionMenu items={rowActions(p)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {p.partyName || 'No payer'} · {p.depositToLedgerName || '—'}
                  </div>
                  {p.deductionTotal > 0 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Gross {money(p.grossAmount)} less {money(p.deductionTotal)} deducted
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <span className="badge">{p.docStatus}</span>
                    
                  </div>
                </div>
              ))}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
              </div>
            )}
          </div>
        )}
      </div>

      <ReceiptVoucherFormModal isOpen={formOpen}
        onPosted={load}
        onClose={() => setFormOpen(false)}
        tabId={tabId} moduleName={moduleName || 'Receipt Vouchers'}
        createInstanceId={createInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef} />

      <ReceiptVoucherViewModal isOpen={viewOpen} receipt={selected}
        onClose={() => { setViewOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Receipt Vouchers'}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default ReceiptVoucherScreen;
